import React, { useState } from "react";
import * as R from "ramda";
import { css } from "glamor";
import { Query, Mutation } from "react-apollo";

import { Icon, Modal, Tooltip, Alert, Checkbox } from "antd";

import SchemaSelect from "@arachnys/styleguide/SchemaSelect";
import { getJsonSchemaFromPath, withApolloState } from "../../util";
import { GET_MONITORING_CONFIG, SENTRY_POLICY_MUTATION } from "./queries";

import SearchIcon from "./searchIcon";

const styles = {
    tooltipContent: css({
        fontSize: 12,
    }),
    tooltipBlock: css({
        marginBottom: 10,
    }),
    tooltipLink: css({
        cursor: "pointer",
        textDecoration: "underline",
    }),
    icon: css({
        width: 20,
        height: 20,
        fill: "#fff",
        marginLeft: 10,
    }),
    iconInactive: css({
        opacity: 0.5,
        cursor: "pointer",
    }),
    modalSelects: css({
        display: "flex",
        justifyContent: "space-between",
        margin: "30px 20px 20px",
        fontSize: 14,
        fontWeight: "bold",
    }),
    frequencyContainer: css({
        width: "170px",
    }),
    monitoringTypeContainer: css({
        width: "225px",
    }),
    modalSelect: css({
        width: "100%",
        marginTop: 5,
    }),
    modalCheckboxContainer: css({
        margin: "10px 20px",
    }),
};

const alertMessage = (
    <>
        Please select type and frequency to start monitoring this entity versus
        World Compliance lists.
        <br />
        To trigger a one-time screening event and stop any previously configured
        monitoring, set the frequency to &quot;Never&quot; and select &quot;Run
        now.&quot;
    </>
);

const getLabelBySchema = (value, schema) => {
    const option = R.find(o => o.value === value)(schema.options);
    return option ? option.label : "";
};

const TooltipContent = ({ monitoring, open, schema }) => {
    const searchEngineSchema = getJsonSchemaFromPath("searchEngine", schema);
    const frequencySchema = getJsonSchemaFromPath("pollingFrequency", schema);
    return (
        <div className={styles.tooltipContent}>
            <div className={styles.tooltipBlock}>
                <strong>This entity is being monitored.</strong>
            </div>

            <div className={styles.tooltipBlock}>
                <div>
                    <strong>Frequency</strong>:&nbsp;
                    <span>
                        {getLabelBySchema(
                            monitoring.pollingFrequency,
                            frequencySchema
                        )}
                    </span>
                </div>
                <div>
                    <strong>Type</strong>:&nbsp;
                    <span>
                        {getLabelBySchema(
                            monitoring.searchEngine,
                            searchEngineSchema
                        )}
                    </span>
                </div>
                <div>
                    <strong>Related entities</strong>:&nbsp;
                    <span>
                        {monitoring.monitorRelatedEntities ? "Yes" : "No"}
                    </span>
                </div>
            </div>

            <div>
                <span className={styles.tooltipLink} onClick={open}>
                    Edit Monitoring
                </span>
            </div>
        </div>
    );
};

const WithTooltip = ({ children, open, monitoring, schema, isMonitored }) => {
    if (isMonitored) {
        return (
            <Tooltip
                placement="right"
                title={
                    <TooltipContent
                        monitoring={monitoring}
                        schema={schema}
                        open={open}
                    />
                }
            >
                {children}
            </Tooltip>
        );
    }

    return children;
};

const modifyFrequencySchema = schema => ({
    ...schema,
    options: schema.options.map(o => ({
        ...o,
        label: o.value === "YEARLY" ? "Yearly" : o.label,
    })),
});

const modifySearchEngineSchema = schema => ({
    ...schema,
    options: schema.options.map(o => ({
        ...o,
        hideOnFrontend: !o.value.startsWith("WORLD_COMPLIANCE_"),
    })),
});

const ModalComponent = ({
    visible,
    toggle,
    monitoring,
    entity,
    refetchEntity,
    schema,
}) => {
    monitoring = monitoring || {};
    const initialData = {
        pollingFrequency: monitoring.pollingFrequency,
        searchEngine: monitoring.searchEngine,
        monitorRelatedEntities: monitoring.monitorRelatedEntities || false,
        status: monitoring.status || "ENABLED",
        entityUlid: entity.key.ulid,
        runNow: false,
    };
    const [formData, setFormData] = useState(initialData);

    const onChange = key => value =>
        setFormData(form => ({ ...form, [key]: value }));

    const onCheckboxChange = key => event => {
        onChange(key)(event.target.checked);
    };

    const onModalClose = () => {
        toggle(false);
        setFormData(initialData);
    };

    const onSubmit = updateCallback => () => {
        const { runNow, ...policyData } = formData;
        updateCallback({
            variables: {
                policy: policyData,
                runNow,
            },
        });
    };

    // Hack to hide BVD from options. This should be replaced by allowing
    // "hide_on_frontend" to be overridden in client config
    const searchEngineSchema = modifySearchEngineSchema(
        getJsonSchemaFromPath("searchEngine", schema)
    );

    const frequencySchema = modifyFrequencySchema(
        getJsonSchemaFromPath("pollingFrequency", schema)
    );

    return (
        <Mutation mutation={SENTRY_POLICY_MUTATION} onCompleted={refetchEntity}>
            {(update, { loading, error }) => (
                <Modal
                    title="Monitoring Setup"
                    visible={visible}
                    onCancel={onModalClose}
                    onOk={onSubmit(update)}
                    okText="Start Monitoring"
                    okButtonProps={{
                        disabled:
                            R.isNil(formData.pollingFrequency) ||
                            R.isNil(formData.searchEngine),
                        loading,
                    }}
                >
                    <Alert showIcon type="info" message={alertMessage} />
                    <div className={styles.modalSelects}>
                        <div className={styles.frequencyContainer}>
                            <div>Monitoring Frequency</div>
                            <SchemaSelect
                                value={formData.pollingFrequency}
                                className={`${styles.modalSelect}`}
                                placeholder="Choose"
                                onChange={onChange("pollingFrequency")}
                                schema={frequencySchema}
                            />
                        </div>
                        <div className={styles.monitoringTypeContainer}>
                            <div>Monitoring Type</div>
                            <SchemaSelect
                                value={formData.searchEngine}
                                className={`${styles.modalSelect}`}
                                schema={searchEngineSchema}
                                placeholder="Choose"
                                onChange={onChange("searchEngine")}
                            />
                        </div>
                    </div>
                    <div className={styles.modalCheckboxContainer}>
                        <Checkbox
                            onChange={onCheckboxChange(
                                "monitorRelatedEntities"
                            )}
                            defaultChecked={initialData.monitorRelatedEntities}
                        >
                            Screen all related parties
                        </Checkbox>
                    </div>
                    <div className={styles.modalCheckboxContainer}>
                        <Checkbox
                            onChange={onCheckboxChange("runNow")}
                            defaultChecked={false}
                        >
                            Run now
                        </Checkbox>
                    </div>
                    {!!error && (
                        <Alert
                            type="error"
                            message={"An error has occurred, please try again"}
                        />
                    )}
                </Modal>
            )}
        </Mutation>
    );
};

export default ({ entity, refetchEntity, schema }) => {
    const [modalVisible, toggleModal] = useState(false);
    const isMonitored = Boolean(entity.continuousMonitoring);

    return (
        <Query fetchPolicy="network-only" query={GET_MONITORING_CONFIG}>
            {withApolloState(apolloProps => {
                if (
                    !apolloProps.data.clientConfig.showContinuousMonitoringModal
                ) {
                    return null;
                }

                return (
                    <>
                        <WithTooltip
                            open={() => toggleModal(true)}
                            monitoring={entity.continuousMonitoring}
                            isMonitored={isMonitored}
                            schema={schema}
                        >
                            <Icon
                                onClick={() =>
                                    !isMonitored && toggleModal(true)
                                }
                                className={`${styles.icon} ${
                                    isMonitored ? "" : styles.iconInactive
                                }`}
                                component={SearchIcon}
                            />
                        </WithTooltip>
                        <ModalComponent
                            visible={modalVisible}
                            toggle={toggleModal}
                            monitoring={entity.continuousMonitoring}
                            schema={schema}
                            entity={entity}
                            refetchEntity={refetchEntity}
                        />
                    </>
                );
            })}
        </Query>
    );
};
